import rg4js from "raygun4js";
import { useMemo, useRef, useState } from "react";
import { Button, Dropdown, Modal, Row, Table } from "react-bootstrap";
import { Link, useHistory } from "react-router-dom";
import { DocumentSubType, Role } from "@/briefpoint-client";
import GeneralInformationScreen from "@/components/ConfirmInfo/GeneralInformation";
import Loading from "@/components/Loading";
import SignUpModal from "@/components/Modals/SignUpModal";
import { useAuth } from "@/hooks/useAuth";
import useConfig, { TEMPLATES_PROPOUNDING_FF } from "@/hooks/useConfig";
import { CaliforniaId } from "@/lib/constants";
import type {
  CaseResponse,
  JurisdictionResponse,
  UserResponse,
} from "@/lib/types";
import { isInTrialOrHasActiveSubscription } from "@/lib/utils";
import { leadAttorneyName } from "@/screens/CaseManagementMulti/case";
import { getCaseSourceLogo } from "@/utils/ExternalPartner/utils";

interface Props {
  addCase: () => void;
  cases: CaseResponse[] | undefined;
  jurisdictions: JurisdictionResponse[] | undefined;
  users: UserResponse[] | null;
  showIfEmpty: boolean;
}

export default function CaseTableMulti({
  addCase,
  cases,
  jurisdictions,
  users,
  showIfEmpty,
}: Props) {
  const { user, firm } = useAuth();
  const [, featureFlags] = useConfig();
  const useTemplatesOnlyPropounding = featureFlags()[TEMPLATES_PROPOUNDING_FF]
    ? featureFlags()[TEMPLATES_PROPOUNDING_FF](user, firm)
    : false;
  const history = useHistory();
  const [isSignUpModalShow, setIsSignUpModalShow] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const pendingDraftType = useRef<DocumentSubType>(DocumentSubType.Unknown);
  const pendingCase = useRef<CaseResponse | null>(null);

  const hasProvidedInfo = useMemo(() => {
    return !!(
      user?.firstName &&
      user?.lastName &&
      user?.barNumber &&
      (!user?.role?.includes(Role.FirmAdmin) ||
        (firm?.name && firm?.address?.street && firm?.address.postalCode))
    );
  }, [
    user?.firstName,
    user?.lastName,
    user?.barNumber,
    user?.role,
    firm?.name,
    firm?.address?.street,
    firm?.address?.postalCode,
  ]);

  function venueDisplay(_case: CaseResponse) {
    const j = jurisdictions?.find((x) => x.id === _case.jurisdiction);
    return j?.venues?.find((x) => x.id === _case.venue)?.shortName ?? "-";
  }

  function getJurisdictionForCase(_case: CaseResponse) {
    return jurisdictions?.find((x) => x.id === _case.jurisdiction);
  }

  function showSignupPage(_case: CaseResponse, value: DocumentSubType) {
    rg4js("trackEvent", {
      type: "pageView",
      path: "/signup-modal",
    });
    pendingDraftType.current = value;
    pendingCase.current = _case;
    setIsSignUpModalShow(true);
  }

  function handleSelectGenerate(
    _case: CaseResponse,
    value: DocumentSubType,
  ): void {
    pendingDraftType.current = DocumentSubType.Unknown;
    // show info page if user has not provided info yet, else generate word document
    if (hasProvidedInfo) {
      if (!user?.isInactive && firm && isInTrialOrHasActiveSubscription(firm)) {
        // Navigate to case page with generate parameter
        history.push({
          pathname: `/cases/${_case.id}`,
          search: `?generate=${value}`,
          state: {
            case: _case,
          },
        });
      } else {
        showSignupPage(_case, value);
      }
    } else {
      pendingDraftType.current = value;
      pendingCase.current = _case;
      setShowInfoModal(true);
    }
  }

  function navigateToCaseWithGenerate(
    _case: CaseResponse,
    value: DocumentSubType,
  ) {
    history.push({
      pathname: `/cases/${_case.id}`,
      search: `?generate=${value}`,
      state: {
        case: _case,
      },
    });
  }

  return (
    <Loading
      isLoading={
        cases === undefined &&
        jurisdictions === undefined &&
        users === undefined
      }
    >
      {(showIfEmpty || (cases?.length !== undefined && cases?.length > 0)) && (
        <div>
          {cases?.length ? (
            <Row>
              <Table striped hover>
                <thead>
                  <tr className="table-header">
                    <th className="table-header-cell" scope="">
                      Title
                    </th>
                    <th className="table-header-cell" scope="">
                      Case Number
                    </th>
                    <th className="table-header-cell" scope="">
                      Venue
                    </th>
                    <th className="table-header-cell" scope="">
                      Lead Attorney
                    </th>
                    <th className="table-header-cell" scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {cases?.map((c) => {
                    return (
                      <tr className="table-row" key={c.id}>
                        <td className="d-flex table-cell gap-2">
                          <div style={{ width: "18px" }}>
                            {getCaseSourceLogo(c.integration?.partner)}
                          </div>
                          <div>{c.shortTitle || c.title}</div>
                        </td>
                        <td className="table-cell">{c.caseNumber ?? "-"}</td>
                        <td className="table-cell">{venueDisplay(c)}</td>
                        <td className="table-cell">
                          {leadAttorneyName(c, users ?? [])}
                        </td>
                        <td className="actions-cell">
                          <div className="d-flex justify-content-end gap-2">
                            {useTemplatesOnlyPropounding && (
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="outline-primary"
                                  size="sm"
                                  id={`draft-dropdown-${c.id}`}
                                  className="btn-26 draft-dropdown-button"
                                >
                                  Draft Request
                                </Dropdown.Toggle>
                                {/* This forces the dropdown to be below the button and not 'flip' based on parent element boundaries. */}
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() =>
                                      handleSelectGenerate(
                                        c,
                                        DocumentSubType.RequestsForAdmission,
                                      )
                                    }
                                  >
                                    Request for Admission
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() =>
                                      handleSelectGenerate(
                                        c,
                                        DocumentSubType.RequestsForProduction,
                                      )
                                    }
                                  >
                                    Request for Production
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() =>
                                      handleSelectGenerate(
                                        c,
                                        DocumentSubType.SpecialInterrogatorries,
                                      )
                                    }
                                  >
                                    {getJurisdictionForCase(c)?.id ===
                                    CaliforniaId
                                      ? "Special Interrogatories"
                                      : "Interrogatories"}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                            <Link
                              data-testid="view-case-button"
                              className="btn btn-primary btn-26"
                              to={{
                                pathname: `/cases/${c.id}`,
                                state: {
                                  case: c,
                                },
                              }}
                            >
                              View Case
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Row>
          ) : (
            <div className="d-flex gap-1 align-items-center">
              There are no Cases yet:
              <Button variant="link" onClick={addCase} className="p-0">
                Add one now
              </Button>
              or just start by uploading a document.
            </div>
          )}
        </div>
      )}
      <SignUpModal
        isShow={isSignUpModalShow}
        onClose={(success) => {
          setIsSignUpModalShow(false);
          if (success && pendingDraftType.current && pendingCase.current) {
            navigateToCaseWithGenerate(
              pendingCase.current,
              pendingDraftType.current,
            );
          }
          pendingDraftType.current = DocumentSubType.Unknown;
          pendingCase.current = null;
        }}
      />
      {!hasProvidedInfo && (
        <Modal
          show={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          size="xl"
          title={"Information for output document"}
          showCancelButton={false}
          showConfirmButton={false}
          preventBackgroundClick
        >
          <GeneralInformationScreen
            documentId={"anything"}
            onSave={async () => {
              setShowInfoModal(false);
              if (pendingDraftType.current && pendingCase.current) {
                navigateToCaseWithGenerate(
                  pendingCase.current,
                  pendingDraftType.current,
                );
              }
              pendingDraftType.current = DocumentSubType.Unknown;
              pendingCase.current = null;
            }}
          />
        </Modal>
      )}
    </Loading>
  );
}
